import React, { useState, useEffect } from "react";
import { Row, Col, Card, Space } from "antd";
import { TagsOutlined } from "@ant-design/icons";
import { useCategories } from "../../../hooks/bot/useCategories";
import { useItems } from "../../../hooks/bot/useItems";
import CategoryList from "../../../components/bot/items/CategoryList";
import ItemSearchBar from "../../../components/bot/items/ItemSearchBar";
import ItemsTable from "../../../components/bot/items/ItemsTable";
import CategoryModal from "./CategoryModal";
import ItemModal from "./ItemModal";
import ItemDetailsDrawer from "./ItemDetailsDrawer";

const ItemsManagement = () => {
  // Custom hooks
  const {
    categories,
    loading: categoriesLoading,
    selectedCategory,
    setSelectedCategory,
    fetchCategories,
    deleteCategory,
  } = useCategories();

  const {
    filteredItems,
    loading: itemsLoading,
    searchText,
    setSearchText,
    fetchItems,
    deleteItem,
  } = useItems(selectedCategory);

  // Modal states
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [itemDrawerVisible, setItemDrawerVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    fetchCategories();
    fetchItems();
  }, [fetchCategories, fetchItems]);

  // Fetch items when category changes
  useEffect(() => {
    fetchItems();
  }, [selectedCategory, fetchItems]);

  // Category handlers
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryModalVisible(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryModalVisible(true);
  };

  // Item handlers
  const handleAddItem = () => {
    setEditingItem(null);
    setItemModalVisible(true);
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setItemDrawerVisible(true);
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        {/* Left Panel - Categories */}
        <Col xs={24} lg={6}>
          <CategoryList
            categories={categories}
            loading={categoriesLoading}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            onAddCategory={handleAddCategory}
            onEditCategory={handleEditCategory}
            onDeleteCategory={deleteCategory}
          />
        </Col>

        {/* Right Panel - Items */}
        <Col xs={24} lg={18}>
          <Card
            title={
              <Space>
                <TagsOutlined />
                <span>
                  Items
                  {selectedCategory && ` in ${selectedCategory.name}`}
                </span>
              </Space>
            }
          >
            <ItemSearchBar
              searchText={searchText}
              onSearchChange={setSearchText}
              onAddItem={handleAddItem}
            />

            <ItemsTable
              items={filteredItems}
              loading={itemsLoading}
              onViewItem={handleViewItem}
              onDeleteItem={deleteItem}
            />
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      <CategoryModal
        visible={categoryModalVisible}
        onCancel={() => {
          setCategoryModalVisible(false);
          setEditingCategory(null);
        }}
        onSuccess={() => {
          setCategoryModalVisible(false);
          setEditingCategory(null);
          fetchCategories();
        }}
        category={editingCategory}
      />

      <ItemModal
        visible={itemModalVisible}
        onCancel={() => {
          setItemModalVisible(false);
          setEditingItem(null);
        }}
        onSuccess={() => {
          setItemModalVisible(false);
          setEditingItem(null);
          fetchItems();
        }}
        item={editingItem}
        categories={categories}
        defaultCategory={selectedCategory}
      />

      <ItemDetailsDrawer
        visible={itemDrawerVisible}
        onClose={() => {
          setItemDrawerVisible(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        onUpdate={fetchItems}
        categories={categories}
      />
    </>
  );
};

export default ItemsManagement;
